import type { JSONContent } from "@tiptap/core";
import type { Database } from "@/lib/database.types";
import type { SubscriptionTier } from "@/types/projects";

export type StoryMode = "chapters" | "continuous";
export type StoryType = "main" | "side";
export type ChapterStatus = "draft" | "in_review" | "final";
export type SideStoryCanonStatus = Database["public"]["Enums"]["canon_status"];
export type StoryTargetKind = "chapter" | "side_story";

export interface StoryRecord {
  id: string;
  project_id: string;
  type: StoryType;
  mode: StoryMode;
  title: string;
  summary: string | null;
  body: JSONContent;
  image_paths: string[];
  canon_status: SideStoryCanonStatus;
  created_at: string;
  updated_at: string;
}

export interface ChapterRecord {
  id: string;
  story_id: string;
  title: string;
  body: JSONContent;
  status: ChapterStatus;
  sort_order: number;
  is_opening: boolean;
  is_closing: boolean;
  image_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface StoryAttachmentView {
  id: string;
  fileName: string;
  imageUrl: string | null;
  path: string;
}

export interface ChapterView {
  id: string;
  storyId: string;
  title: string;
  body: JSONContent;
  status: ChapterStatus;
  statusLabel: string;
  sortOrder: number;
  isOpening: boolean;
  isClosing: boolean;
  imagePath: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  hasHeaderImage: boolean;
  headerImageUrl: string | null;
  headerTargetLabel: string;
  headerGenerateActionLabel: string;
}

export interface MainStoryView {
  id: string;
  projectId: string;
  mode: StoryMode;
  modeLabel: string;
  title: string;
  summary: string;
  body: JSONContent;
  chapters: ChapterView[];
  createdAt: string;
  updatedAt: string;
}

export interface SideStoryView {
  id: string;
  projectId: string;
  canonStatus: SideStoryCanonStatus;
  canonStatusLabelKey: string;
  mode: "continuous";
  modeLabel: string;
  title: string;
  summary: string;
  body: JSONContent;
  attachments: StoryAttachmentView[];
  createdAt: string;
  updatedAt: string;
}

export interface StoryWorkspaceView {
  mainStory: MainStoryView | null;
  sideStories: SideStoryView[];
  tier: SubscriptionTier;
}

export interface StoryCapacitySummary {
  tier: SubscriptionTier;
  chapterCount: number;
  chapterLimit: number | null;
  chaptersRemaining: number | null;
  sideStoryCount: number;
  sideStoryLimit: number | null;
  sideStoriesRemaining: number | null;
  isChapterUnlimited: boolean;
  isSideStoryUnlimited: boolean;
}

export interface StoryLimitPrompt {
  reason: "chapter_limit_reached" | "side_story_limit_reached";
  tier: SubscriptionTier;
  currentCount: number;
  allowedCount: number;
  recommendedTier: SubscriptionTier;
}

export interface CreateMainStoryInput {
  body?: JSONContent;
  mode: StoryMode;
  summary?: string;
  title: string;
}

export interface UpdateMainStoryInput {
  body: JSONContent;
  summary: string;
}

export interface CreateChapterInput {
  body?: JSONContent;
  imagePath?: string | null;
  status?: ChapterStatus;
  title: string;
}

export interface UpdateChapterInput {
  body: JSONContent;
  imagePath?: string | null;
  status: ChapterStatus;
  title: string;
}

export interface CreateSideStoryInput {
  body?: JSONContent;
  canonStatus?: SideStoryCanonStatus;
  imagePaths?: string[];
  summary: string;
  title: string;
}

export interface UpdateSideStoryInput {
  body: JSONContent;
  canonStatus: SideStoryCanonStatus;
  imagePaths?: string[];
  summary: string;
  title: string;
}

export interface StoryExportTemplateView {
  id: string;
  description: string;
  label: string;
  minimumTier: SubscriptionTier;
}

export interface StoryExportRequestInput {
  projectId: string;
  targetId: string;
  targetKind: StoryTargetKind;
  templateId: string;
}

export interface StoryExportResult {
  blob: Blob;
  fileName: string;
}

export interface StoryLoreOption {
  id: string;
  title: string;
}

export interface StoryReferenceView {
  id: string;
  broken: boolean;
  canonStatusLabel: string;
  loreEntryId: string;
  title: string;
}

export interface StructuralAnalysisAvailability {
  eligible: boolean;
  reason: string | null;
}

export type StructuralAnalysisScopeSelection =
  | {
      scopeKind: "full-story";
    }
  | {
      endChapterId: string;
      scopeKind: "chapter-range";
      startChapterId: string;
    };

export const storyModes: StoryMode[] = ["chapters", "continuous"];
export const storyModeLabels: Record<StoryMode, string> = {
  chapters: "Chapter-based",
  continuous: "Continuous",
};

export const chapterStatuses: ChapterStatus[] = ["draft", "in_review", "final"];
export const chapterStatusLabels: Record<ChapterStatus, string> = {
  draft: "Draft",
  in_review: "In Review",
  final: "Final",
};

export const sideStoryCanonStatuses: SideStoryCanonStatus[] = [
  "draft",
  "proposed",
  "canon",
  "retconned",
];

export const sideStoryCanonStatusLabels: Record<SideStoryCanonStatus, string> = {
  draft: "lore.statuses.draft",
  proposed: "lore.statuses.proposed",
  canon: "lore.statuses.canon",
  retconned: "lore.statuses.retconned",
};

export function cloneEmptyStoryDocument(): JSONContent {
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
      },
    ],
  };
}

export function createSideStoryDraft(): SideStoryView {
  return {
    id: "draft-side-story",
    projectId: "",
    canonStatus: "draft",
    canonStatusLabelKey: sideStoryCanonStatusLabels.draft,
    mode: "continuous",
    modeLabel: storyModeLabels.continuous,
    title: "",
    summary: "",
    body: cloneEmptyStoryDocument(),
    attachments: [],
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  };
}

export function formatStructuralAnalysisRangeLabel(
  startSortOrder: number,
  endSortOrder: number,
) {
  const startLabel = startSortOrder + 1;
  const endLabel = endSortOrder + 1;

  if (startLabel === endLabel) {
    return `Chapter ${startLabel}`;
  }

  return `Chapters ${startLabel} to ${endLabel}`;
}
